import { observable, computed } from 'mobx';
import { createFormField } from '../../../lib/form';
import {
  ArtistMappingSchema,
  ArtistSchema,
  PlaylistGroupPlaylistSchema,
  PricingCalculatorSchema,
  RatecardsSchema,
  TrackSchema,
} from '../../../types/schema';
import { UICreator, UIHeroArtist, UIHeroGenre, UIHeroTheme } from '../../../types/ui';
import { FindFiltersInput } from '../../../types/graphql';
import findFilters from '../../../lib/findFilters';
import { HeroSearchInputSuggestion } from '../../../components/hero/search-input-suggestions';

export class HomePageModel {
  @observable
  query = 'Sleaford Mods';

  @computed
  get nextQuestionIndex(): number {
    switch (this.question) {
      // artist -> theme
      case 0:
        return 2;
      // genre -> artist
      case 1:
        return 0;
      // theme -> genre
      case 2:
        return 1;
    }
  }

  @computed
  get searchMoreText(): string {
    if (!this.currentQuery || !this.currentQuery.length) {
      return null;
    }

    switch (this.question) {
      case 0:
        return this.query;
      case 1:
        return this.genres.find((g) => g.slug === this.selectedGenres[0]).label;
      case 2:
        return this.themes.find((t) => t.slug === this.selectedTheme).label;
    }
  }

  @computed
  get searchMoreHref(): string {
    if (!this.currentQuery || !this.currentQuery.length || !this.currentQuery[0].length) {
      return '/search';
    }

    switch (this.question) {
      case 0:
        return '/search?query=' + this.query;
      case 1:
        return '/search?genre=' + this.selectedGenres[0];
      case 2:
        return '/search?theme=' + this.selectedTheme;
    }

    return '/search';
  }

  @computed
  get currentQuery(): Array<string> {
    switch (this.question) {
      case 0:
        return [this.query];
      case 1:
        return this.selectedGenres;
      case 2: {
        const theme = this.themes.find((theme) => theme.slug === this.selectedTheme);
        if (theme) return [theme.label];
        return [];
      }
      default:
        return [];
    }
  }

  @observable
  searchState = {
    query: '',
  };

  @computed
  get searchConfig() {
    switch (this.question) {
      case 1:
        return {
          distinct: 1,
          query: this.currentQuery.join(' '),
          hitsPerPage: 30,
          restrictSearchableAttributes: ['tags.hookd_genre.name', 'tags.hookd_genre.slug'],
        };

      default:
        return {
          distinct: 1,
          query: this.query,
          hitsPerPage: 30,
          restrictSearchableAttributes: ['artists.name'],
        };
    }
  }

  @computed
  get mappingsConfig() {
    return {
      distinct: 1,
      query: this.query,
    };
  }

  @computed
  get fallbackConfig() {
    return {
      distinct: 1,
      query: '',
      hitsPerPage: 30,
      filters: this.fallbackGenres.map((q) => `tags.hookd_genre.name:"${q}"`).join(' OR '),
    };
  }

  @computed
  get fallbackArtist(): ArtistMappingSchema {
    return this.mappings.length ? this.mappings[0] : null;
  }

  @computed
  get fallbackGenres(): Array<string> {
    return this.mappings.length ? this.mappings[0].genres : [];
  }

  @computed
  get showFallback(): boolean {
    return this.results.length === 0;
  }

  @observable
  filters: FindFiltersInput = findFilters({
    artist: 'High Contrast',
  });

  @observable
  results: Array<TrackSchema> = [];

  @observable
  mappings: Array<ArtistMappingSchema> = [];

  @observable
  fallbackResults: Array<TrackSchema> = [];

  @observable
  showResults = false;

  @observable
  showSplash = true;

  @observable
  cycleCarousel = false;

  @observable
  question = 0;

  @observable
  heroEmpty: string = null;

  @observable
  resetImpulse = false;

  @computed
  get heroImage(): string {
    return this.selectedArtist ? this.selectedArtist.image : this.heroEmpty;
  }

  @computed
  get heroArtist(): string {
    return (this.selectedArtist && this.selectedArtist.name) || '';
  }

  @observable
  loading: boolean;

  @observable
  loadingResults: boolean;

  @observable
  favouriteTracks: TrackSchema[];

  @observable
  favouriteArtists: ArtistSchema[];

  @observable
  carousels: (TrackSchema[] | PlaylistGroupPlaylistSchema[])[];

  @observable
  featuredTracks: Array<TrackSchema>;

  @observable
  resultsVisible: boolean;

  @observable
  pricingImage: string;

  @observable ctaActionLabel: string;
  @observable ctaContent: string;
  @observable ctaTitle: string;

  @observable endingSoonTitle: string;
  @observable justAddedTitle: string;
  @observable homepageThirdCarouselTitle: string;
  @observable homepageFourthCarouselTitle: string;
  @observable homepageFifthCarouselTitle: string;
  @observable homepageSixthCarouselTitle: string;
  @observable homepageSeventhCarouselTitle: string;

  @observable question1: string;
  @observable question1Next: string;
  @observable question2: string;
  @observable question2Next: string;
  @observable question3: string;
  @observable question3Next: string;
  @observable questionSubmit: string;

  @observable section1Title: string;
  @observable section1Content: string;
  @observable section1ActionLabel: string;
  @observable section1ActionTarget: string;
  @observable section1Image1: string;
  @observable section1Image2: string;
  @observable section1Image3: string;

  @observable section2Title: string;
  @observable section2Subtitle1: string;
  @observable section2Subtitle2: string;
  @observable section2Content1: string;
  @observable section2Content2: string;
  @observable section2Image: string;

  @observable section3Title: string;
  @observable section3Subtitle1: string;
  @observable section3Subtitle2: string;
  @observable section3Content1: string;
  @observable section3Content2: string;
  @observable section3Image: string;

  @observable videoId: string;

  @observable creatorsTitle: string;
  @observable creatorsViewCTA: string;

  @observable creators: Array<UICreator> = [];
  @observable genres: Array<UIHeroGenre> = [];
  @observable themes: Array<UIHeroTheme> = [];

  @observable selectedGenres: Array<string> = [];
  @observable selectedTheme: string;

  @observable heroSuggestions: Array<HeroSearchInputSuggestion> = [];

  /** @deprecated **/
  @observable artists: Array<UIHeroArtist> = [];
  @observable artist = 0;

  @computed get selectedArtist(): UIHeroArtist {
    return this.artists.length && this.artists.length > this.artist ? this.artists[this.artist] : null;
  }

  @observable mailchimpEmail = createFormField({
    name: 'EMAIL',
    label: 'Email address',
    value: '',
    autoComplete: 'email',
  });

  @observable mailchimpAction: string;
  @observable mailchimpHidden: string;

  @computed get mailchimpForm() {
    return {
      action: this.mailchimpAction,
      hidden: this.mailchimpHidden,
      email: this.mailchimpEmail,
    };
  }

  @observable pricingChannelId = '';
  @observable pricingLoading = false;
  @observable pricingError: string = null;
  @observable pricingResult: PricingCalculatorSchema = null;
  @observable pricingCurrency: { value: string; label: string } = {
    value: 'USD',
    label: '$ USD',
  };

  @observable pricingCurrencies: Array<{ value: string; label: string }> = [
    // TODO use from prismic
    {
      value: 'USD',
      label: '$ USD',
    },
    {
      value: 'EUR',
      label: '€ EUR',
    },
    {
      value: 'GBP',
      label: '£ GBP',
    },
  ];

  @observable pricingBackgroundImage;

  @computed get pricing() {
    return {
      channelId: this.pricingChannelId,
      loading: this.pricingLoading,
      error: this.pricingError,
      result: this.pricingResult,
      backgroundImage: this.pricingBackgroundImage,
      currency: this.pricingCurrency,
      currencies: this.pricingCurrencies,
    };
  }

  @observable ratecards: RatecardsSchema;

  @observable
  ratecardTypes: Array<{ value: string; label: string }> = [
    { value: 'standard', label: 'Standard rate' },
    { value: 'brand_sponsored', label: 'Brand sponsored rate' },
  ];

  @observable
  ratecardType = 'standard';

  @observable
  ratecardLoading = false;

  @observable benefits: Array<{
    title: string;
    content: any;
  }> = [];

  @observable showPricingGrid = false;
}
